import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseISO, startOfMonth } from 'date-fns';
import { CalendarClockIcon, SearchIcon, XCircleIcon } from 'lucide-react';
import { useStudio } from '../contexts/StudioContext';
import { vehicleSizes } from '../data/schedule';
import { site } from '../data/site';
import { Booking } from '../types/booking';
import { dayState, slotsForDate } from '../utils/availability';
import { duration, euro, longDate, timeRange } from '../utils/format';
import { MonthCalendar } from '../components/booking/MonthCalendar';
import { StatusBadge } from '../components/admin/StatusBadge';
export function Appointment() {
  const [params, setParams] = useSearchParams();
  const {
    bookings,
    services,
    extras,
    settings,
    findByReference,
    reschedule,
    setStatus
  } = useStudio();
  const [query, setQuery] = useState(params.get('ref') ?? '');
  const [error, setError] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [pick, setPick] = useState<{
    date: string | null;
    time: string | null;
  }>({
    date: null,
    time: null
  });
  const current: Booking | null = useMemo(() => {
    const ref = params.get('ref');
    return ref ? findByReference(ref) : null;
  }, [params, findByReference]);
  const service = current ? services.find(s => s.id === current.serviceId) ?? null : null;
  const chosen = current ? extras.filter(e => current.extraIds.includes(e.id)) : [];
  const size = current ? vehicleSizes.find(v => v.id === current.vehicleSizeId) : null;
  const slots = useMemo(() => pick.date && current ? slotsForDate(pick.date, current.durationMinutes, bookings, settings) : [], [pick.date, current, bookings, settings]);
  const search = (e: React.FormEvent) => {
    e.preventDefault();
    const found = findByReference(query);
    if (!found) {
      setError('Wij vinden geen afspraak met deze referentie. Controleer uw bevestigingsmail.');
      return;
    }
    setError(null);
    setParams({
      ref: found.reference
    });
    setMoving(false);
    setPick({
      date: null,
      time: null
    });
    setMonth(startOfMonth(parseISO(found.date)));
  };
  return <section className="mx-auto max-w-3xl px-5 pb-24 pt-24 sm:px-6 sm:pt-32 lg:pb-32 lg:pt-40" data-id="element-996" data-mp="7x82kfj0seb9">
      <span className="flex items-center gap-3" data-id="element-997" data-mp="cxm9fu8vxi7p">
        <span className="hairline h-px w-10" aria-hidden="true" data-id="element-998" data-mp="cuzsx540nsdc" />
        <span className="text-[11px] uppercase tracking-[0.28em] text-ember" data-id="element-999" data-mp="gy68x4gxkxx0">Mijn afspraak</span>
      </span>
      <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.03] tracking-tightest text-chalk sm:text-5xl" data-id="element-1000" data-mp="lwg4vhahc2jo">
        Afspraak bekijken, verzetten of annuleren.
      </h1>

      <form onSubmit={search} className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-1001" data-mp="vmivjau4p4eu">
        <label htmlFor="ref" className="sr-only" data-id="element-1002" data-mp="10nq4hd80c9n3">
          Referentienummer
        </label>
        <input id="ref" value={query} onChange={e => setQuery(e.target.value)} placeholder="Bijv. DCC-4F2KX" className="flex-1 rounded-xl border border-edge bg-carbon px-4 py-3.5 text-sm text-chalk placeholder:text-dim/60 focus:border-ember" data-id="element-1003" data-mp="14o0h38qb1yax" />
        <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-ember px-6 py-3.5 text-sm font-semibold text-void transition-[background-color,box-shadow] duration-200 ease-smooth hover:bg-white hover:glow" data-id="element-1004" data-mp="2g535uucen7nt">
          <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-1005" data-mp="33vpcqiaoalw8" />
          Zoeken
        </button>
      </form>
      {error && <p className="mt-3 text-sm text-red-400" data-id="element-1006" data-mp="2z3yy404pdbz0">{error}</p>}

      {current && <div className="mt-10 overflow-hidden rounded-2xl border border-edge bg-carbon" data-id="element-1007" data-mp="2u5xene3d7an0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-edge px-6 py-5" data-id="element-1008" data-mp="3rl1l5fy6epxh">
            <span data-id="element-1009" data-mp="3mul4lmz26o6w">
              <span className="block text-xs uppercase tracking-[0.16em] text-dim" data-id="element-1010" data-mp="3mu1l41trfrqr">
                Referentie {current.reference}
              </span>
              <span className="mt-1 block font-display text-xl font-extrabold tracking-tight text-chalk" data-id="element-1011" data-mp="37eee550csge1">
                {service?.name}
              </span>
            </span>
            <StatusBadge status={current.status} data-id="element-1012" data-mp="2xfoy7hs0p60f" />
          </div>

          <dl className="divide-y divide-edge text-sm" data-id="element-1013" data-mp="32cfdw3zwtzlb">
            <div className="flex justify-between gap-6 px-6 py-4" data-id="element-1014" data-mp="32f1usu8oxrg7">
              <dt className="text-dim" data-id="element-1015" data-mp="3q3bcpl2s9v22">Wanneer</dt>
              <dd className="text-right font-medium capitalize text-chalk" data-id="element-1016" data-mp="3v1mk7p5lvth4">
                {longDate(current.date)} · {timeRange(current.time, current.durationMinutes)}
              </dd>
            </div>
            <div className="flex justify-between gap-6 px-6 py-4" data-id="element-1017" data-mp="3lbjin0kf5776">
              <dt className="text-dim" data-id="element-1018" data-mp="3l9qafgu5qex2">Voertuig</dt>
              <dd className="text-right font-medium text-chalk" data-id="element-1019" data-mp="cv88zxyq1xmf">
                {current.customer.car} · {size?.label}
              </dd>
            </div>
            {chosen.length > 0 && <div className="flex justify-between gap-6 px-6 py-4" data-id="element-1020" data-mp="22clp0nq1mnl0">
                <dt className="text-dim" data-id="element-1021" data-mp="1xebtgc0epkyv">Extra's</dt>
                <dd className="text-right font-medium text-chalk" data-id="element-1022" data-mp="1xdsixgd35yuv">{chosen.map(e => e.name).join(', ')}</dd>
              </div>}
            <div className="flex justify-between gap-6 px-6 py-4" data-id="element-1023" data-mp="2743m30oy440p">
              <dt className="text-dim" data-id="element-1024" data-mp="274wwcbn45bnl">Duur</dt>
              <dd className="text-right font-medium text-chalk" data-id="element-1025" data-mp="1je0wqscf1c5s">{duration(current.durationMinutes)}</dd>
            </div>
            <div className="flex justify-between gap-6 px-6 py-4" data-id="element-1026" data-mp="1eghjnvrgf5ff">
              <dt className="text-dim" data-id="element-1027" data-mp="1sez5tp1po9v8">Waar</dt>
              <dd className="text-right font-medium text-chalk" data-id="element-1028" data-mp="1sgsd96k5ey4a">{site.address}</dd>
            </div>
            <div className="flex items-center justify-between gap-6 bg-panel px-6 py-5" data-id="element-1029" data-mp="1nekvu1gjupuk">
              <dt className="text-dim" data-id="element-1030" data-mp="33t2vij19nbuh">Totaal</dt>
              <dd className="font-display text-2xl font-extrabold tracking-tight text-ember" data-id="element-1031" data-mp="2yvb55tg9rxse">
                {euro(current.amount)}
              </dd>
            </div>
          </dl>

          {current.status !== 'cancelled' && current.status !== 'completed' && <div className="border-t border-edge px-6 py-5" data-id="element-1032" data-mp="21f52ijjbcp64">
              <div className="flex flex-wrap gap-3" data-id="element-1033" data-mp="25ibgyegkxmno">
                <button type="button" onClick={() => {
            setMoving(m => !m);
            setMonth(startOfMonth(parseISO(current.date)));
          }} className="inline-flex items-center gap-2 rounded-full border border-chalk/20 px-5 py-3 text-sm font-medium text-chalk transition-colors duration-200 ease-smooth hover:border-ember hover:text-ember" data-id="element-1034" data-mp="25gqmeou9m3pt">
                  <CalendarClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-1035" data-mp="1rjttmhul6c6a" />
                  Verzetten
                </button>
                <button type="button" onClick={() => setStatus(current.id, 'cancelled')} className="inline-flex items-center gap-2 rounded-full border border-edge px-5 py-3 text-sm font-medium text-dim transition-colors duration-200 ease-smooth hover:border-red-400/50 hover:text-red-300" data-id="element-1036" data-mp="u1bptm5yxdv8">
                  <XCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-1037" data-mp="hjr6li7634ny" />
                  Annuleren
                </button>
              </div>

              {moving && <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_240px]" data-id="element-1038" data-mp="8juef6lkjno5">
                  <MonthCalendar month={month} onMonthChange={setMonth} selected={pick.date} onSelect={iso => setPick({
            date: iso,
            time: null
          })} stateFor={date => dayState(date, current.durationMinutes, bookings, settings)} minMonth={new Date()} data-id="element-1039" data-mp="3tnpf5n5a15d" />
                  <div data-id="element-1040" data-mp="3gzxn9fof3ie2">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-dim" data-id="element-1041" data-mp="3gznu5j82sv45">Nieuwe tijd</h2>
                    <div className="mt-4 grid grid-cols-2 gap-2.5 lg:grid-cols-1" data-id="element-1042" data-mp="18qy3yqysue4e">
                      {slots.map(slot => <button key={slot.time} type="button" disabled={!slot.available} onClick={() => setPick(p => ({
                ...p,
                time: slot.time
              }))} className={`rounded-xl border px-4 py-3 text-sm transition-colors duration-200 ease-smooth ${pick.time === slot.time ? 'border-ember bg-ember text-void' : slot.available ? 'border-edge bg-panel text-chalk hover:border-ember/50' : 'cursor-not-allowed border-edge/50 text-dim/40'}`} data-id="element-1043" data-mp="1dpsl7oh4bume">
                          {slot.time}
                        </button>)}
                      {slots.length === 0 && <p className="rounded-xl border border-dashed border-edge px-4 py-5 text-sm text-dim" data-id="element-1044" data-mp="3okt5y7mesoco">
                          Kies een dag in de agenda.
                        </p>}
                    </div>
                    <button type="button" disabled={!pick.date || !pick.time} onClick={() => {
              reschedule(current.id, pick.date!, pick.time!);
              setMoving(false);
              setPick({
                date: null,
                time: null
              });
            }} className="mt-5 w-full rounded-full bg-ember px-5 py-3.5 text-sm font-semibold text-void transition-opacity duration-200 ease-smooth disabled:opacity-40" data-id="element-1045" data-mp="3tjvzovwxv85c">
                      Verzetten bevestigen
                    </button>
                  </div>
                </div>}
            </div>}
        </div>}

      {!current && !error && <p className="mt-8 rounded-2xl border border-dashed border-edge px-6 py-6 text-sm leading-relaxed text-dim" data-id="element-1046" data-mp="11z9fcqpg0nca">
          Vul uw referentienummer in uit de bevestigingsmail. Liever persoonlijk contact? Bel {site.phone} of stuur
          een WhatsApp-bericht.
        </p>}
    </section>;
}